import * as types from '../mutation-types';
import { reject, propEq } from 'ramda';
import { RootState, NotificationState, NotificationId, NotificationOptions, Notification } from '../types';
import { ActionContext } from 'vuex';

let index = 0;
const defaultTimeout = 5000;

// initial state
const initialState: NotificationState = {
  notifications: [],
  hold: false,
};

// getters
const getters = {};

// actions
const actions = {
  add({ commit, state, dispatch }: ActionContext<NotificationState, RootState>, notification: NotificationOptions) {
    const activeNotification =
      notification.id && state.notifications.find((storedNotification) => storedNotification.id === notification.id);

    if (activeNotification) {
      // A notification with a custom id can only exist once, but will reset its timer when it is added again
      dispatch('resetRemovalTimeout', activeNotification);

      return notification.id;
    }

    const id = notification.id || index++;
    const undoAction = notification.undo
      ? () =>
          notification.undo(dispatch).then(() => {
            dispatch('clearRemovalTimeout', id);
            commit(types.NOTIFICATION_REMOVE, id);
          })
      : null;

    if (notification.timeout !== false) {
      dispatch('scheduleRemoval', { notification, id });
    }

    let timeout: number | false = defaultTimeout;

    if (notification.timeout !== undefined) {
      timeout = notification.timeout;
    } else if (notification.undo) {
      // increase timeout for undo-able actions (mostly delete)
      timeout = 15000;
    }

    commit(types.NOTIFICATION_ADD, {
      ...notification,
      testId: notification.testId ? `notification-${notification.testId}` : null,
      timeout,
      actions: notification.actions
        ? notification.actions.map((action) => ({
            label: action.label,
            action: () => {
              // Pass a function into the action handler that allows the creator of the notification
              // to close it after the action has executed
              const closeFunction = () => dispatch('remove', id);

              action.action(closeFunction);
            },
          }))
        : [],
      id,
      undoAction,
      position: notification.position || 'right',
    });

    return id;
  },

  clearRemovalTimeout(
    { state }: ActionContext<NotificationState, RootState>,
    notification: Notification | NotificationId
  ) {
    let timeoutId: ReturnType<typeof setTimeout> = null;

    if (typeof notification === 'string' || typeof notification === 'number') {
      timeoutId = state.notifications.find((storedNotification) => storedNotification.id === notification).timeoutId;
    } else {
      timeoutId = notification.timeoutId;
    }

    clearTimeout(timeoutId);
  },

  /**
   * Update an existing notification by id. Only the message can be updated at this point
   */
  update(
    { commit, state }: ActionContext<NotificationState, RootState>,
    notification: Pick<Notification, 'id' | 'message'>
  ) {
    const activeNotification =
      notification.id && state.notifications.find((storedNotification) => storedNotification.id === notification.id);

    if (activeNotification) {
      commit(types.NOTIFICATION_UPDATE, {
        id: notification.id,
        message: notification.message,
      });
    }
  },

  remove({ commit }: ActionContext<NotificationState, RootState>, id: NotificationId) {
    commit(types.NOTIFICATION_REMOVE, id);
  },

  hold({ state, commit }: ActionContext<NotificationState, RootState>) {
    state.notifications.forEach((notification) => {
      clearTimeout(notification.timeoutId);
    });

    commit(types.NOTIFICATION_HOLD);
  },

  resetRemovalTimeout({ dispatch }: ActionContext<NotificationState, RootState>, notification: Notification) {
    dispatch('clearRemovalTimeout', notification);

    dispatch('scheduleRemoval', { notification, id: notification.id });
  },

  unhold({ state, commit, dispatch }: ActionContext<NotificationState, RootState>) {
    commit(types.NOTIFICATION_UNHOLD);

    state.notifications.forEach((notification) => {
      dispatch('scheduleRemoval', { notification, id: notification.id });
    });
  },

  scheduleRemoval(
    { commit }: ActionContext<NotificationState, RootState>,
    { notification, id }: { notification: Notification; id: NotificationId }
  ) {
    if (notification.timeout !== false) {
      notification.timeoutId = setTimeout(() => {
        commit(types.NOTIFICATION_REMOVE, id);
      }, notification.timeout || defaultTimeout);
    }
  },
};

// mutations
const mutations = {
  [types.NOTIFICATION_HOLD](state: NotificationState) {
    state.hold = true;
  },

  [types.NOTIFICATION_UNHOLD](state: NotificationState) {
    state.hold = false;
  },

  [types.NOTIFICATION_ADD](state: NotificationState, notification: Notification) {
    state.notifications.push(notification);
  },

  [types.NOTIFICATION_UPDATE](state: NotificationState, notification: Notification) {
    state.notifications = state.notifications.map((storedNotification) => {
      if (storedNotification.id === notification.id) {
        return {
          ...storedNotification,
          message: notification.message,
        };
      }

      return storedNotification;
    });
  },

  [types.NOTIFICATION_REMOVE](state: NotificationState, id: NotificationId) {
    state.notifications = reject<Notification>(propEq('id', id), state.notifications);
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
